import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useMemo, useState } from "react";

import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import {
	useGetImportedConversationsQuery,
	useImportEarlierConversationMutation,
} from "@/pages/Private/redux/conversation/conversation.api";
import {
	selectImportedConversations,
	selectImportedShared,
	selectImportedTruncated,
	selectImportResult,
} from "@/pages/Private/redux/conversation/conversation.slice";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";
import { useSingleFlight } from "@/utils/useSingleFlight";

import {
	earlierConversationResultLines,
	EarlierConversationResultLine,
} from "./earlierConversationResult.util";
import { mergeImportedIntoThread } from "./importedConversationThread.util";

export interface EarlierConversation {
	/** The lead's thread with the viewer's imported messages woven in. */
	thread: Conversation[] | undefined;
	/** What the last import for this lead answered, one sentence per source. */
	resultLines: EarlierConversationResultLine[];
	/** The import request itself failed for this lead (not one source: the whole call). */
	requestFailed: boolean;
	/** Whether what the viewer loads here is shared with the team; null until known. */
	shared: boolean | null;
	/** The server showed only the newest imported messages; older ones were left out. */
	truncated: boolean;
	/** An import for this lead is running. */
	isLoading: boolean;
	/** Another import is running; a second one is not started meanwhile. */
	isBusy: boolean;
	load: () => void;
}

/**
 * "Load earlier conversation" for one lead (PREC-4305): the viewer's imported history with the
 * lead, merged into the thread, and the on-demand import that adds to it.
 *
 * Every answer is kept with the lead it was asked for (apps/web/CLAUDE.md §23): the history and
 * the import result live in the slice bucketed by list person, and a failed request is remembered
 * with its list person id — a lead switched to while an import runs never shows the other lead's
 * outcome.
 */
export const useEarlierConversation = (
	listPersonId: number | null | undefined,
	conversations: Conversation[] | undefined
): EarlierConversation => {
	const { user } = useAppSelector(authSelector);

	useGetImportedConversationsQuery(listPersonId ?? skipToken);

	const imported = useAppSelector((state) => selectImportedConversations(state, listPersonId));
	const truncated = useAppSelector((state) => selectImportedTruncated(state, listPersonId));
	const shared = useAppSelector((state) => selectImportedShared(state, listPersonId));
	const result = useAppSelector((state) => selectImportResult(state, listPersonId));
	const [importEarlierConversation] = useImportEarlierConversationMutation();
	const { run, isRunning } = useSingleFlight();
	const [pendingFor, setPendingFor] = useState<number | null>(null);
	const [failedFor, setFailedFor] = useState<number | null>(null);

	const thread = useMemo(
		() =>
			mergeImportedIntoThread(conversations, imported, {
				id: user?.id ?? 0,
				firstName: user?.firstName,
				lastName: user?.lastName,
			}),
		[conversations, imported, user?.id, user?.firstName, user?.lastName]
	);

	const resultLines = useMemo(
		() => (result ? earlierConversationResultLines(result) : []),
		[result]
	);

	const load = useCallback(() => {
		if (!listPersonId) {
			return;
		}

		void run(async () => {
			setPendingFor(listPersonId);
			setFailedFor((previous) => (previous === listPersonId ? null : previous));

			try {
				await importEarlierConversation(listPersonId).unwrap();
			} catch {
				// The call failed as a whole: say so for this lead. It is never "nothing found".
				setFailedFor(listPersonId);
			} finally {
				setPendingFor(null);
			}
		});
	}, [importEarlierConversation, listPersonId, run]);

	return {
		thread,
		resultLines,
		shared,
		truncated,
		requestFailed: !!listPersonId && failedFor === listPersonId,
		isLoading: !!listPersonId && pendingFor === listPersonId,
		isBusy: isRunning,
		load,
	};
};
